import { SiteType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { SiteSelectedFilter } from '~constants/enums';

export const getVisibilityOptions = (): FilterMenuOption[] => [
  { label: t('common.internal'), value: 'internal' },
  { label: t('common.external'), value: 'external' },
];

export type Filters = {
  departments?: string[];
  siteTypes?: SiteType[];
  visibility?: string[];
};

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
const sitesFilters: Array<
  Extract<keyof Filters, 'departments' | 'siteTypes' | 'visibility'>
> = ['departments', 'siteTypes', 'visibility'];
type SitesFilters = (typeof sitesFilters)[number];
type SitesFilterKey = SitesFilters;

type AppliedFiltersState = Record<
  SiteSelectedFilter,
  {
    sitesFilterKey: SitesFilterKey;
    search: string;
  }
>;

export const sitesFilterKeyBySelectedFilter: Record<SiteSelectedFilter, SitesFilterKey> =
  {
    [SiteSelectedFilter.DEPARTMENTS]: 'departments',
    [SiteSelectedFilter.SITE_TYPES]: 'siteTypes',
    [SiteSelectedFilter.VISIBILITY]: 'visibility',
  };

type FiltersAvailableOptionsState = Record<SiteSelectedFilter, FilterMenuOption[]>;

const getAppliedFiltersInitialState = () => {
  const initialState = Object.entries(sitesFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as SiteSelectedFilter;

      obj[key] = {
        sitesFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as SiteSelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'sites_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<SiteSelectedFilter, FilterMenuOption[]>
>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  // Ensure visibility options are included if they are empty or missing in the local storage
  if (
    !parsed[SiteSelectedFilter.VISIBILITY] ||
    parsed[SiteSelectedFilter.VISIBILITY].length === 0
  ) {
    parsed[SiteSelectedFilter.VISIBILITY] = getVisibilityOptions();
  }

  const initialState = {
    ...Object.values(SiteSelectedFilter).reduce((obj, filter) => {
      obj[filter] = parsed[filter] ?? [];

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  initialState.siteTypes = Object.values(SiteType).map((type) => ({
    label: t(`site_types.${type}`),
    value: `${type}`,
  }));

  return initialState;
};

export default function useSitesFilters() {
  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersInitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter = sitesFilterKeyBySelectedFilter[filter as SiteSelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      obj[key as SiteSelectedFilter] = options;

      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [JSON.stringify(availableOptions)]);

  return {
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  };
}
